import { useMemo } from "react";
import { useState, useCallback, useEffect, useRef } from "react";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { useRequestFarmerVerificationOTP } from "../hooks/useSubmitFieldWork";
function showAlert({ title, message, isError = false }) {
  confirmAlert({
    title,
    message,
    closeOnEscape: true,
    closeOnClickOutside: true,
    buttons: [
      {
        label: "OK",
        style: isError
          ? { background: "#e53e3e", color: "#fff" }
          : { background: "#494395", color: "#fff" },
      },
    ],
  });
}

const GEO_ERROR_MESSAGES = {
  1: "Permission denied. Please allow location access.",
  2: "Position unavailable. Unable to determine GPS location.",
  3: "Request timed out. Please try again.",
};

export function useMortalityMetrics(lastHeadCount, todayBirdHeadCount) {
  return useMemo(() => {
    const hasValidCounts =
      typeof lastHeadCount === "number" &&
      typeof todayBirdHeadCount === "number" &&
      lastHeadCount > 0;

    const deadBirdsToday = hasValidCounts
      ? Math.max(lastHeadCount - todayBirdHeadCount, 0)
      : 0;

    const cumulativeMortality = hasValidCounts
      ? ((deadBirdsToday / lastHeadCount) * 100).toFixed(2)
      : "0.00";

    const cumulativeSurvival = (100 - parseFloat(cumulativeMortality)).toFixed(
      2,
    );

    return { deadBirdsToday, cumulativeMortality, cumulativeSurvival };
  }, [lastHeadCount, todayBirdHeadCount]);
}

export function useGeolocationCapture(onCapture) {
  const [capturing, setCapturing] = useState(false);

  const capture = useCallback(() => {
    if (!navigator.geolocation) {
      showAlert({
        title: "Not Supported",
        message: "Geolocation is not supported on this device.",
        isError: true,
      });
      return;
    }

    setCapturing(true);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        onCapture(`${latitude}, ${longitude}`);
        setCapturing(false);
      },
      (error) => {
        showAlert({
          title: "GPS Error",
          message:
            GEO_ERROR_MESSAGES[error.code] ??
            "Unable to retrieve GPS coordinates.",
          isError: true,
        });
        setCapturing(false);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 10000 },
    );
  }, [onCapture]);

  return { capturing, capture };
}

export function usePhotoEvidence(onChange) {
  const [preview, setPreview] = useState(null);
  const previewRef = useRef(null);

  useEffect(() => {
    previewRef.current = preview;
  }, [preview]);

  useEffect(() => {
    return () => {
      if (previewRef.current) URL.revokeObjectURL(previewRef.current);
    };
  }, []);

  const setFile = useCallback(
    (file) => {
      if (previewRef.current) URL.revokeObjectURL(previewRef.current);
      const url = URL.createObjectURL(file);
      setPreview(url);
      onChange(file);
    },
    [onChange],
  );

  const clearFile = useCallback(() => {
    if (previewRef.current) URL.revokeObjectURL(previewRef.current);
    setPreview(null);
    onChange(null);
  }, [onChange]);

  return { preview, setFile, clearFile };
}
export function useOtpVerification() {
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [code, setCode] = useState("");

  const requestOTP = useRequestFarmerVerificationOTP();

  const send = useCallback(
    async (mobile) => {
      if (!mobile) {
        showAlert({
          title: "Missing Number",
          message: "Please enter the contact's mobile number first.",
          isError: true,
        });
        return;
      }

      setSending(true);
      try {
        const result = await requestOTP({
          action: "request_otp",
          contact_phonenumber: mobile,
        });

        if (result.error) {
          showAlert({
            title: "OTP Failed",
            message: result.error,
            isError: true,
          });
        } else {
          setSent(true);
          showAlert({
            title: "OTP Sent",
            message: "Verification code sent successfully.",
          });
        }
      } catch {
        showAlert({
          title: "Error",
          message: "Failed to send verification code.",
          isError: true,
        });
      } finally {
        setSending(false);
      }
    },
    [requestOTP],
  );

  const setCodeDigitsOnly = useCallback((value) => {
    setCode(value.replace(/\D/g, ""));
  }, []);

  return { sent, sending, code, send, setCode: setCodeDigitsOnly };
}
export const AVERAGE_WEIGHT_OPTIONS = Array.from({ length: 25 }, (_, i) => {
  const weight = ((i + 1) * 0.1).toFixed(1);
  return { label: `${weight} kg`, value: weight };
});

export const VET_OFFICERS = ["Wilkins Otieno", "Rodney Saad"];
